<template>
  <div id="app">
    <router-view />

    <div v-if="showAuthOverlay" class="wechat-auth-mask">
      <div class="wechat-auth-card">
        <template v-if="authLoading">
          <div class="wechat-auth-icon loading"></div>
          <h3>微信登录中</h3>
          <p>正在获取微信登录信息，请稍候...</p>
        </template>

        <template v-else-if="showWechatBrowserTip">
          <div class="wechat-auth-icon">微信</div>
          <h3>请在微信浏览器打开</h3>
          <p>当前页面需要在微信内完成授权登录后才能继续使用。</p>
        </template>

        <template v-else>
          <div class="wechat-auth-icon">微</div>
          <h3>请先登录微信</h3>
          <p>检测到本地没有 `openId` 信息，请先完成微信授权登录。</p>
          <button class="wechat-login-btn" @click="goWechatLogin">微信授权登录</button>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import axios from "axios";
import { persistWechatUserProfile } from "@/assets/js/wechatAuth";

const WECHAT_APP_ID = "wx5a24ab8112103f52";
const WECHAT_REDIRECT_URI = "http://www.wenhuaxunli.com";
const WECHAT_SCOPE = "snsapi_userinfo";
const WECHAT_STATE = "STATE";
const OPEN_ID_KEY = "openId";
const LOGIN_REDIRECT_KEY = "wechat_login_redirect";
const EXEMPT_PATHS = ["/mbti-result", "/hld-result", "/muit-result"];

export default {
  name: "app",
  data() {
    return {
      authLoading: false,
      showWechatBrowserTip: false,
      showLoginPanel: false,
    };
  },
  computed: {
    showAuthOverlay() {
      return this.authLoading || this.showWechatBrowserTip || this.showLoginPanel;
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler() {
        this.syncWechatAuth();
      },
    },
  },
  methods: {
    isExemptRoute(route) {
      const currentPath = route && route.path ? route.path : "";
      return EXEMPT_PATHS.indexOf(currentPath) > -1;
    },
    isWechatBrowser() {
      return /micromessenger/i.test(window.navigator.userAgent || "");
    },
    getStoredOpenId() {
      return localStorage.getItem(OPEN_ID_KEY) || localStorage.getItem("openid") || "";
    },
    getCodeFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get("code") || "";
    },
    extractOpenId(payload) {
      if (!payload) return "";

      if (payload.openId) return String(payload.openId);
      if (payload.openid) return String(payload.openid);
      if (payload.data && payload.data.openId) return String(payload.data.openId);
      if (payload.data && payload.data.openid) return String(payload.data.openid);

      return "";
    },
    clearOauthQuery() {
      const url = new URL(window.location.href);
      url.searchParams.delete("code");
      url.searchParams.delete("state");
      const nextUrl = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, "", nextUrl);
    },
    getWechatRedirectUri() {
      const currentPath = this.$route && this.$route.fullPath ? this.$route.fullPath : "/";
      const redirectUrl = new URL(currentPath, WECHAT_REDIRECT_URI);
      redirectUrl.searchParams.delete("code");
      redirectUrl.searchParams.delete("state");
      return redirectUrl.toString();
    },
    async syncWechatAuth() {
      const code = this.getCodeFromUrl();
      if (code) {
        this.showWechatBrowserTip = false;
        this.showLoginPanel = false;
        await this.loginByWechatCode(code);
        return;
      }

      if (this.isExemptRoute(this.$route)) {
        this.authLoading = false;
        this.showWechatBrowserTip = false;
        this.showLoginPanel = false;
        return;
      }

      if (!this.isWechatBrowser()) {
        this.authLoading = false;
        this.showWechatBrowserTip = true;
        this.showLoginPanel = false;
        return;
      }

      this.showWechatBrowserTip = false;

      if (this.getStoredOpenId()) {
        this.authLoading = false;
        this.showLoginPanel = false;
        return;
      }

      this.authLoading = false;
      this.showLoginPanel = true;
    },
    async loginByWechatCode(code) {
      this.authLoading = true;
      this.showLoginPanel = false;

      try {
        const response = await axios.get(`${this.$api}/system/web/getInfoByCode`, {
          params: { code: code },
        });
        const res = response && response.data ? response.data : {};
        const userProfile = persistWechatUserProfile(res);
        const openId = userProfile.openid || this.extractOpenId(res);

        if (Number(res.code) === 200 && openId) {
          localStorage.setItem(OPEN_ID_KEY, openId);
          localStorage.setItem("openid", openId);
          if (res.data) {
            localStorage.setItem("wechat_login_info", JSON.stringify(res.data));
          }

          this.clearOauthQuery();

          this.authLoading = false;
          this.showLoginPanel = false;

          const redirectPath = localStorage.getItem(LOGIN_REDIRECT_KEY);
          if (redirectPath) {
            localStorage.removeItem(LOGIN_REDIRECT_KEY);
            if (redirectPath !== this.$route.fullPath) {
              this.$router.replace(redirectPath);
            }
          }
          return;
        }

        this.authLoading = false;
        this.showLoginPanel = true;
        this.$message.error(res.msg || "微信登录失败，请重新授权");
      } catch (error) {
        this.authLoading = false;
        this.showLoginPanel = true;
        this.$message.error("微信登录失败，请稍后重试");
      }
    },
    goWechatLogin() {
      const currentPath = this.$route.fullPath || "/";
      localStorage.setItem(LOGIN_REDIRECT_KEY, currentPath);
      const redirectUri = this.getWechatRedirectUri();
      const authUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${WECHAT_APP_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${WECHAT_SCOPE}&state=${WECHAT_STATE}#wechat_redirect`;
      window.location.href = authUrl;
    },

  },
};
</script>
<style lang="scss">
html,
body,
#app {
  width: 100%;
}
/* 顶部导航栏 */
.nav-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 15px;
  padding-top: 20px;
  position: relative;
  z-index: 10;
}
.back-icon {
  font-size: 20px;
  font-weight: bold;
  color: #333;
}
.title {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}
.capsule {
  display: flex;
  align-items: center;
  border-radius: 15px;
  padding: 4px 10px;
}
.capsule .dot {
  width: 4px;
  height: 4px;
  background: #333;
  border-radius: 50%;
  margin: 0 2px;
}
.capsule .divider {
  width: 1px;
  height: 12px;
  background: rgba(0, 0, 0, 0.2);
  margin: 0 8px;
}
.capsule .circle {
  width: 12px;
  height: 12px;
  border: 2px solid #333;
  border-radius: 50%;
}
.wechat-auth-mask {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.45);
}
.wechat-auth-card {
  width: 100%;
  max-width: 320px;
  padding: 28px 22px;
  border-radius: 18px;
  background: #fff;
  text-align: center;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.14);
}
.wechat-auth-card h3 {
  margin: 0 0 10px;
  font-size: 18px;
  color: #1f2d3d;
}
.wechat-auth-card p {
  margin: 0;
  font-size: 14px;
  line-height: 1.7;
  color: #66707a;
}
.wechat-auth-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6adf69 0%, #3cbc63 100%);
  color: #fff;
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.wechat-auth-icon.loading {
  position: relative;
  background: #eef7ee;
}
.wechat-auth-icon.loading::after {
  content: "";
  width: 24px;
  height: 24px;
  border: 3px solid #7fd37d;
  border-top-color: transparent;
  border-radius: 50%;
  animation: wechat-spin 0.8s linear infinite;
}
.wechat-login-btn {
  width: 100%;
  height: 42px;
  margin-top: 18px;
  border: none;
  border-radius: 21px;
  background: linear-gradient(90deg, #7ed957 0%, #3fc56a 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
}
@keyframes wechat-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

